import React from 'react';
import { Button } from 'src/components/ui/button';
import { Badge } from 'src/components/ui/badge';
import { NoteEditForm } from './NoteEditForm';
import { NoteDisplay } from './NoteDisplay';
import {
  CheckCircleIcon,
  PencilSimpleIcon,
  LightningIcon,
  XCircleIcon,
  PencilIcon,
} from '@phosphor-icons/react';
import type { UseImproveNoteOptions } from '@/hooks/useImproveNote';
import { useImproveNote } from '@/hooks/useImproveNote';
import { cn } from '@/lib/utils';
import { Alert } from '../common/alert';

export interface ImproveNoteContentProps {
  noteId: string | null;
  onClose?: () => void;
  options?: UseImproveNoteOptions;
}

export function ImproveNoteContent({
  noteId,
  onClose,
  options,
}: ImproveNoteContentProps) {
  const {
    originalNote,
    improveResult,
    improvedNoteText,
    improveError,
    isLoadingImprovements,
    isLoadingNote,
    isEditingImprovedNote,
    hasUserEdits,
    acceptEdit,
    rejectEdit,
    handleEditImprovedNote,
    handleSaveEditedNote,
    handleCancelEditNote,
    isAccepting,
  } = useImproveNote(noteId, {
    onSuccess: onClose,
    ...options,
  });

  const handleClose = () => {
    rejectEdit();
    onClose?.();
  };

  if (isLoadingNote) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <span className="ml-2 text-sm text-gray-600">Loading note...</span>
      </div>
    );
  }

  if (improveError) {
    const errorMessage =
      improveError instanceof Error
        ? improveError.message
        : 'Failed to improve note with AI';
    return (
      <Alert
        title="Error improving note"
        description={errorMessage}
        type="error"
      />
    );
  }

  if (!originalNote && !isLoadingImprovements) {
    return <div className="text-center py-8 text-gray-500">Note not found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Loading State */}
      {isLoadingImprovements && (
        <div className="space-y-6 py-8">
          <div className="flex flex-col items-center text-center space-y-4">
            <div className="relative">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <LightningIcon className="absolute inset-0 m-auto h-6 w-6 text-blue-600" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-medium">Analyzing Your Note</h3>
              <p className="text-sm text-gray-600 max-w-md">
                Our AI is carefully reviewing your note content and identifying
                ways to:
              </p>
              <ul className="text-sm text-gray-600 space-y-1 text-left max-w-md">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                  Improve clarity and structure
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                  Add actionable details
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                  Enhance searchability
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="h-4 w-4 text-green-600 flex-shrink-0" />
                  Optimize for future reference
                </li>
              </ul>
            </div>
          </div>

          {/* Original Note Preview */}
          {originalNote && (
            <div className="border-t pt-6">
              <div className="text-sm font-medium mb-2">Original Note:</div>
              <div className="p-3 bg-gray-50 rounded-md border text-sm max-h-40 overflow-y-auto">
                <NoteDisplay showFullContent content={originalNote.note} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Results State */}
      {improveResult && originalNote && (
        <div className="space-y-6">
          {/* Confidence Score */}
          <div className="flex items-center gap-2">
            <Badge
              variant={improveResult.confidence > 0.7 ? 'default' : 'secondary'}
            >
              {Math.round(improveResult.confidence * 100)}% Confidence
            </Badge>
          </div>

          {/* Improvements Made */}
          <div>
            <div className="text-sm font-medium">Improvements Made:</div>
            <ul className="mt-2 space-y-1">
              {improveResult.improvements.map((improvement, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <CheckCircleIcon className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                  {improvement}
                </li>
              ))}
            </ul>
          </div>

          {/* Before and After Comparison */}
          <div
            className={cn(
              'flex gap-4',
              isEditingImprovedNote
                ? 'flex-col'
                : 'flex-col md:flex-row md:grid md:grid-cols-2',
            )}
          >
            <div>
              <div className="text-sm font-medium">Original Note:</div>
              <div className="mt-2 p-3 bg-gray-50 rounded-md border text-sm max-h-60 overflow-y-auto">
                <NoteDisplay
                  showFullContent
                  content={improveResult.originalNote}
                />
              </div>
            </div>
            <div>
              <div className="text-sm font-medium flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span>Improved Note:</span>
                  {hasUserEdits && (
                    <Badge variant="secondary" className="text-xs">
                      <PencilIcon className="h-3 w-3 mr-1" />
                      Edited
                    </Badge>
                  )}
                </div>
                {!isEditingImprovedNote && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleEditImprovedNote}
                    className="ml-2"
                  >
                    <PencilSimpleIcon className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                )}
              </div>
              <div className="mt-2">
                {isEditingImprovedNote && (
                  <NoteEditForm
                    initialValue={improvedNoteText}
                    metadata={originalNote?.metadata}
                    onSave={(content) => handleSaveEditedNote(content)}
                    onCancel={handleCancelEditNote}
                    placeholder="Edit the improved note..."
                    height={250}
                    allowMetadataRemoval={false}
                  />
                )}
                {!isEditingImprovedNote && (
                  <div
                    className="p-3 bg-green-50 rounded-md border text-sm max-h-60 overflow-y-auto cursor-pointer hover:bg-green-100 transition-colors"
                    onClick={handleEditImprovedNote}
                    title="Click to edit"
                  >
                    <NoteDisplay showFullContent content={improvedNoteText} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-2 pt-4 border-t">
        <Button
          variant="outline"
          onClick={handleClose}
          disabled={isLoadingImprovements}
        >
          <XCircleIcon className="h-4 w-4 mr-1" />
          {improveResult ? 'Discard' : 'Cancel'}
        </Button>
        {improveResult && (
          <Button
            onClick={acceptEdit}
            disabled={
              isAccepting || isLoadingImprovements || isEditingImprovedNote
            }
          >
            <CheckCircleIcon className="h-4 w-4 mr-1" />
            {isAccepting
              ? 'Applying...'
              : hasUserEdits
                ? 'Apply AI + Your Edits'
                : 'Apply AI Improvements'}
          </Button>
        )}
      </div>
    </div>
  );
}
